<!--
  @SkillSelectDialog  Modal dialog (Classical parchment design) to pick a new skill for the current hero.
  Left: the passed skills grouped by category; right: the SkillCard of the selected skill.
  Props: `id` of the <dialog> (open it with command="show-modal"), `skills` list of protoSkills to choose from,
         `cats` ordered category keys for grouping, `onadd(skillID)` called by "Fertigkeit hinzufügen".
-->

<script>
	import {view} from "../client.svelte.js";
	import {categories} from "../core.js";
	import SkillCard from "../elements/SkillCard.svelte";

	let {id = 'add-skill-dialog', skills = [], cats = Object.keys(categories), onadd} = $props()

	let dialog
	let selID = $state(null)
	let hideSecondary = $state(false)

	/** secondary skills ("Nebenfertigkeiten") are cheap skills (cost < 50); domains are never secondary **/
	const is_secondary = skill => !skill.isDomain && skill.cost < 50
	let shown = $derived(skills.filter(skill => !(hideSecondary && is_secondary(skill))))

	let groups = $derived(
		cats
			.map(cat => ({cat, skills: shown.filter(skill => skill.cat === cat).sort((a, b) => a.name.localeCompare(b.name))}))
			.filter(group => group.skills.length > 0) )
	let current = $derived(shown.find(skill => skill.ID === selID) ?? groups[0]?.skills[0] ?? null)

	function group_title(group) {
		if (group.skills.every(skill => skill.isDomain))
			return categories[group.cat]
		return `${categories[group.cat]}${(['G', 'GP', 'GI', 'GS'].includes(group.cat)) ? " " : "-"}Fertigkeiten`
	}

	function add_skill() {
		if (current)
			onadd?.(current.ID)
		selID = null
		dialog.close()
	}
</script>


<!-------------------------------- HTML -------------------------------------->

<dialog bind:this={dialog} {id} class="sheet-dialog classical paper-aged" aria-label="Fertigkeit auswählen"
        onclick={e => (e.target === dialog) && dialog.close()}>
	<header class="skill-head">
		<div>
			<div class="kicker">{view.character?.name}</div>
			<h2 class="skill-title">Fertigkeit auswählen</h2>
		</div>
		<button class="close fa icon-xmark" title="Schließen" aria-label="Schließen" onclick={() => dialog.close()}></button>
	</header>
	<div class="double-rule"></div>

	<div class="skill-body">
		<div class="skill-side">
			<button class="toggle" role="switch" aria-checked={hideSecondary} onclick={() => hideSecondary = !hideSecondary}>
				<span class="switch" class:on={hideSecondary}><span class="knob"></span></span>
				<span class="toggle-label">Nebenfertigkeiten ausblenden</span>
			</button>
			<div class="skill-list">
				{#each groups as group}
					<div class="skill-group">
						<div class="kicker group-title">{group_title(group)}</div>
						{#each group.skills as skill}
							<button class="skill-item" class:selected={current?.ID === skill.ID} class:domain={skill.isDomain}
							        onclick={() => selID = skill.ID}>
								<span class="skill-name">{skill.name}</span>
								<span class="skill-traits">{skill.traits?.join(" · ")}</span>
							</button>
						{/each}
					</div>
				{:else}
					<p class="muted">Keine weiteren Fertigkeiten verfügbar.</p>
				{/each}
			</div>
		</div>

		{#if current}
			{#key current.ID}
				<SkillCard skill={current}/>
			{/key}
		{/if}
	</div>

	<footer class="skill-foot">
		<button class="btn-outline cancel" onclick={() => dialog.close()}>Abbrechen</button>
		<button class="btn-outline add" disabled={!current} onclick={add_skill}>
			<span class="fa icon-plus"></span>Fertigkeit hinzufügen
		</button>
	</footer>
</dialog>


<!-------------------------------- STYLE -------------------------------------->

<style lang="sass">
	dialog
		box-sizing: border-box
		width: min(1072px, calc(100vw - 48px))
		max-height: calc(100vh - 48px)
		padding: 32px 48px 36px
		border: 1px solid var(--color-accent-300)
		border-radius: var(--radius-lg)
		box-shadow: 0 20px 48px rgba(0, 0, 0, .6)
		overflow: auto

		&::backdrop
			background: rgba(0, 0, 0, .65)

	/* header */
	.skill-head
		display: flex
		align-items: flex-end
		justify-content: space-between
		gap: 24px

	.skill-title
		margin-top: 2px
		font-weight: 400
		font-size: 40px
		line-height: 1
		letter-spacing: -.01em

	.close
		align-self: flex-start
		width: 36px
		height: 36px
		padding: 0
		border: none
		border-radius: var(--radius-md)
		background: none
		color: var(--color-accent)
		font-size: 18px
		cursor: pointer

		&:hover
			background: color-mix(in srgb, var(--color-accent) 10%, transparent)

	.double-rule
		height: 4px
		margin: 16px 0 20px
		border-top: 1px solid var(--color-accent)
		border-bottom: 1px solid var(--color-accent-300)

	/* body: skill list left, skill card right */
	.skill-body
		display: grid
		grid-template-columns: 296px 640px
		gap: 38px
		align-items: start

	.skill-side
		display: flex
		flex-direction: column
		gap: 14px

	/* toggle for hiding secondary skills */
	.toggle
		display: flex
		align-items: center
		gap: 10px
		padding: 0 8px 10px 0
		border: none
		border-bottom: 1px solid var(--color-divider)
		border-radius: 0
		background: none
		text-align: left
		cursor: pointer
		user-select: none

	.switch
		flex: none
		box-sizing: border-box
		display: flex
		justify-content: flex-start
		width: 30px
		height: 16px
		padding: 2px
		border: 1px solid var(--color-neutral-400)
		border-radius: 8px

		.knob
			width: 10px
			height: 10px
			border-radius: 50%
			background: var(--color-neutral-500)

		&.on
			justify-content: flex-end
			border-color: var(--color-accent)
			background: color-mix(in srgb, var(--color-accent) 18%, transparent)

			.knob
				background: var(--color-accent-800)

	.toggle-label
		font-size: 14px
		color: var(--color-neutral-700)

	.skill-list
		height: 520px
		overflow-y: auto
		padding-right: 10px
		scrollbar-width: thin
		scrollbar-color: var(--color-accent-400) transparent

	.skill-group
		margin-bottom: 18px

	.group-title
		padding-bottom: 6px
		border-bottom: 1px solid var(--color-accent-300)

	.skill-item
		display: flex
		align-items: baseline
		gap: 10px
		width: 100%
		padding: 7px 8px
		border: none
		border-bottom: 1px solid var(--color-divider)
		border-radius: 0
		background: none
		text-align: left
		cursor: pointer

		&:hover
			background: color-mix(in srgb, var(--color-accent) 7%, transparent)

		&.selected
			background: color-mix(in srgb, var(--color-accent) 14%, transparent)
			cursor: default

			.skill-name
				color: var(--color-accent-800)
				text-decoration: underline var(--color-accent)
				text-underline-offset: 3px

	.skill-name
		flex: 1
		font-size: 16px

	.skill-item.domain .skill-name
		font-weight: 600

	.skill-traits
		font-family: var(--font-heading)
		font-size: 12px
		font-weight: 600
		letter-spacing: .06em
		color: var(--color-accent-700)

	/* footer */
	.skill-foot
		display: flex
		align-items: center
		justify-content: space-between
		gap: 16px
		margin-top: 28px
		padding-top: 18px
		border-top: 1px solid var(--color-divider)

	.add
		display: inline-flex
		align-items: center
		gap: 8px

		.fa
			font-size: 11px
</style>
